import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { PortfolioConfig } from '../types/config';
import BackgroundAnimation from './BackgroundAnimation';
import ImageCarousel from './ImageCarousel';

interface AboutProps {
  config: PortfolioConfig;
}

// Handwritten Typewriter Component
interface HandwrittenTypewriterProps {
  text: string;
  speed?: number;
  delay?: number;
}

const HandwrittenTypewriter: React.FC<HandwrittenTypewriterProps> = ({ 
  text, 
  speed = 50, 
  delay = 0 
}) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsTyping(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  useEffect(() => {
    if (!isTyping || currentIndex >= text.length) return;

    const timer = setTimeout(() => {
      const currentChar = text[currentIndex];
      setDisplayText(prev => prev + currentChar);
      setCurrentIndex(prev => prev + 1);
    }, speed + Math.random() * 30); // Add some randomness for more natural handwriting

    return () => clearTimeout(timer);
  }, [currentIndex, text, speed, isTyping]);

  return (
    <div className="relative">
      <div className="font-handwriting text-2xl md:text-3xl text-dark-700 dark:text-dark-200 leading-relaxed">
        {displayText.split('\n').map((line, index) => (
          <React.Fragment key={index}>
            {line}
            {index < displayText.split('\n').length - 1 && <br />}
          </React.Fragment>
        ))}
        <motion.span
          animate={{ opacity: [1, 0, 1] }}
          transition={{ duration: 0.8, repeat: Infinity }}
          className="inline-block w-0.5 h-8 bg-dark-700 dark:bg-dark-200 ml-1"
        />
      </div>
    </div>
  );
};

const About: React.FC<AboutProps> = ({ config }) => {
  const [showTypewriter, setShowTypewriter] = useState(false);

  useEffect(() => {
    // Start the typewriter animation after the component mounts
    const timer = setTimeout(() => {
      setShowTypewriter(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <section id="about" className="section-padding bg-white dark:bg-dark-900 relative overflow-hidden">
      {/* Animated Background */}
      {/* <BackgroundAnimation variant="waves" intensity="subtle" /> */}
      
      <div className="container-custom relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            About <span className="gradient-text">Me</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-600 to-purple-600 mx-auto"></div>
        </motion.div>

        {/* Carousel and Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Personal Images Carousel */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            {config.personal.images && config.personal.images.length > 0 && (
              <ImageCarousel 
                images={config.personal.images} 
                autoPlayInterval={4000}
                showControls={true}
              />
            )}
          </motion.div>

          {/* Right Column - Title and Description */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="flex flex-col justify-start h-full pt-8"
          >
            <div className="text-dark-600 dark:text-dark-300 leading-relaxed">
              {showTypewriter ? (
                <HandwrittenTypewriter 
                  text={config.about.description} 
                  speed={40}
                  delay={500}
                />
              ) : (
                <div className="h-24 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-dark-700 dark:to-dark-600 rounded animate-pulse"></div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About; 